import { ReactNode } from "react";

export function SectionHeader({ index, title, rule }: { index: string; title: string; rule?: string }) {
  return (
    <div className="mb-4 mt-8 flex items-baseline gap-3 border-b border-[var(--line)] pb-2 first:mt-0">
      <span className="font-mono text-[11px] text-[var(--muted-2)]">{index}</span>
      <h2 className="font-display text-[15px] font-medium">{title}</h2>
      {rule && <span className="ml-auto font-mono text-[10px] text-[var(--muted-2)]">{rule}</span>}
    </div>
  );
}

function Wrap({ label, rule, required, children, hint }: { label: string; rule?: string; required?: boolean; children: ReactNode; hint?: string }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="flex items-baseline justify-between text-[12.5px] text-[var(--muted)]">
        <span>
          {label}
          {required && <span className="ml-0.5 text-[var(--alert)]">*</span>}
        </span>
        {rule && <span className="font-mono text-[10px] text-[var(--muted-2)]">{rule}</span>}
      </span>
      {children}
      {hint && <span className="text-[11px] text-[var(--muted-2)]">{hint}</span>}
    </label>
  );
}

const inputClass =
  "rounded-sm border border-[var(--line)] bg-white px-3 py-2 text-[13.5px] outline-none transition-colors placeholder:text-[var(--muted-2)] focus:border-[var(--blue)]";

export function TextField(props: {
  label: string;
  rule?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  hint?: string;
  mono?: boolean;
}) {
  return (
    <Wrap label={props.label} rule={props.rule} required={props.required} hint={props.hint}>
      <input
        type={props.type ?? "text"}
        className={`${inputClass} ${props.mono ? "font-mono" : ""}`}
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        required={props.required}
      />
    </Wrap>
  );
}

export function SelectField(props: {
  label: string;
  rule?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  hint?: string;
}) {
  return (
    <Wrap label={props.label} rule={props.rule} required={props.required} hint={props.hint}>
      <select className={inputClass} value={props.value} onChange={(e) => props.onChange(e.target.value)} required={props.required}>
        <option value="">Select…</option>
        {props.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </Wrap>
  );
}

export function CheckField(props: { label: string; rule?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5 rounded-sm border border-[var(--line)] bg-white px-3 py-2.5">
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(e) => props.onChange(e.target.checked)}
        className="mt-0.5 accent-[var(--blue)]"
      />
      <span className="text-[12.5px] text-[var(--muted)]">
        {props.label}
        {props.rule && <span className="ml-1.5 font-mono text-[10px] text-[var(--muted-2)]">{props.rule}</span>}
      </span>
    </label>
  );
}

export function Grid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>;
}

/** CNIC input: the applicant types digits only (no dashes) — dashes are
 * inserted automatically as they type, and the backend normalizes again on
 * save regardless, so this is purely a UX convenience, not the source of truth. */
export function CnicField(props: {
  label?: string;
  rule?: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  function formatCnic(raw: string) {
    const digits = raw.replace(/\D/g, "").slice(0, 13);
    const p1 = digits.slice(0, 5);
    const p2 = digits.slice(5, 12);
    const p3 = digits.slice(12, 13);
    return [p1, p2, p3].filter(Boolean).join("-");
  }
  return (
    <Wrap
      label={props.label ?? "CNIC / SECP Registration No."}
      rule={props.rule}
      required={props.required}
      hint={props.hint ?? "Digits only — dashes are added automatically."}
    >
      <input
        type="text"
        inputMode="numeric"
        className={`${inputClass} font-mono`}
        value={props.value}
        placeholder="00000-0000000-0"
        maxLength={15}
        onChange={(e) => props.onChange(formatCnic(e.target.value))}
        required={props.required}
      />
    </Wrap>
  );
}

/** Simple file upload field for document requirements (Conformity
 * Assessment, Security Clearance, notary attestation, etc.). Shows the
 * chosen filename; passes the raw File up so the caller can attach it to a
 * multipart submission. */
export function FileUploadField(props: {
  label: string;
  rule?: string;
  required?: boolean;
  file: File | null;
  onChange: (f: File | null) => void;
  hint?: string;
  accept?: string;
}) {
  return (
    <Wrap label={props.label} rule={props.rule} required={props.required} hint={props.hint}>
      <label className="flex cursor-pointer items-center gap-3 rounded-sm border border-dashed border-[var(--line)] bg-white px-3 py-2.5 text-[12.5px] text-[var(--muted)] transition-colors hover:border-[var(--blue)]">
        <span className="rounded-sm bg-[#0b5fa512] px-2 py-1 text-[11px] font-medium text-[var(--blue)]">Choose file</span>
        <span className="truncate">{props.file ? props.file.name : "No file selected"}</span>
        <input
          type="file"
          accept={props.accept}
          className="hidden"
          onChange={(e) => props.onChange(e.target.files?.[0] ?? null)}
        />
      </label>
    </Wrap>
  );
}
